<template>
  <div class="role-details">
    <div class="page-header">
      <Breadcrumb>
        <BreadcrumbItem to="/roles">{{ this.$t('admin.roles') }}</BreadcrumbItem>
        <BreadcrumbItem>{{ operTitle }}</BreadcrumbItem>
      </Breadcrumb>
    </div>
    <Card shadow class="detail">
      <Form ref="record" :model="record" :rules="isDetail && !isEdit ? {} : rules">
        <Row>
          <i-col span="16">
            <FormItem :label="$t('admin.roleName')" prop="name">
              <i-input v-model="record.name" size="large" :disabled="isDetail && !isEdit"></i-input>
            </FormItem>
          </i-col>
          <i-col span="16">
            <FormItem :label="$t('common.description')" prop="description">
              <i-input
                v-model="record.description"
                size="large"
                :disabled="isDetail && !isEdit"
                type="textarea"
              ></i-input>
            </FormItem>
          </i-col>
        </Row>
      </Form>
      <Spin v-if="loading" size="large" fix> </Spin>
    </Card>
    <div class="permission-title">{{ $t('admin.permissionList') }}</div>
    <Card shadow class="permission-card">
      <Tabs v-model="currentTab" @on-click="findCurrentPermission" :animated="false">
        <TabPane v-for="tab in authTabs" :key="tab" :label="tab" :name="tab">
          <div class="tab-content">
            <Table v-if="currentTab === tab" :columns="permissionCol" :data="currentPermission"></Table>
          </div>
        </TabPane>
      </Tabs>
    </Card>
    <Button v-if="!isDetail || isEdit" type="primary" @click="save">{{ $t('common.submit') }}</Button>
    <Button :style="!isDetail ? 'margin-left: 8px' : ''" type="text" to="/roles">
      {{ $t('common.cancel') }}
    </Button>
  </div>
</template>

<script lang="jsx">
import { loadApis } from '@/api/auth'
import { createRole, viewRole, updateRole } from '@/api/admin'

export default {
  name: 'RoleDetails',
  data() {
    return {
      id: this.$route.params.id,
      loading: false,
      allMethods: ['POST', 'GET', 'PUT', 'DELETE'],
      permissionList: [],
      currentTab: 'roles',
      rolePolicies: [],
      currentPermission: [],
      authTabs: [],
      permissionCol: [
        {
          title: 'API',
          key: 'path',
          minWidth: 150,
        },
        {
          title: this.$t('admin.create'),
          key: 'POST',
          render: (h, params) => {
            return this.renderAuthList({ row: params.row, type: 'POST' })
          },
        },
        {
          title: this.$t('admin.get'),
          key: 'GET',
          render: (h, params) => {
            return this.renderAuthList({ row: params.row, type: 'GET' })
          },
        },
        {
          title: this.$t('admin.update'),
          key: 'PUT',
          render: (h, params) => {
            return this.renderAuthList({ row: params.row, type: 'PUT' })
          },
        },
        {
          title: this.$t('admin.delete'),
          key: 'DELETE',
          render: (h, params) => {
            return this.renderAuthList({ row: params.row, type: 'DELETE' })
          },
        },
      ],
      permissionOption: [
        {
          value: 'never',
          label: 'never',
        },
        {
          value: 0,
          label: 'always',
        },
        {
          value: 1,
          label: 'owned',
        },
      ],
      record: {
        policies: {},
      },
      rules: {
        name: [{ required: true, message: this.$t('common.fillContent') }],
      },
    }
  },

  computed: {
    isDetail() {
      return this.id !== '0' && this.$route.query.oper !== 'create'
    },
    isEdit() {
      return this.id !== '0' && this.$route.query.oper === 'edit'
    },
    operTitle() {
      if (this.isEdit) {
        return this.$t('common.edit')
      }
      if (this.isDetail) {
        return this.$t('common.view')
      }
      return this.$t('common.create')
    },
  },

  created() {
    this.loadPermissionList()
  },

  methods: {
    loadPermissionList() {
      this.loading = true
      loadApis()
        .then((res) => {
          this.loading = false
          this.permissionList = res.data
          this.authTabs = this.permissionList.map((auth) => auth.name)
          this.currentTab = this.authTabs[0]
          this.currentPermission = this.permissionList[0].content
          this.record.policies = this.parsePoliciesToShow('all', 'never')
          if (this.id !== '0') {
            this.loadDetail()
          }
        })
        .catch(() => {
          this.loading = false
        })
    },
    loadDetail() {
      viewRole(this.id)
        .then((res) => {
          const { name, description, policies } = res.data
          this.$set(this.record, 'name', name)
          this.$set(this.record, 'description', description)
          this.setPolicies(policies)
        })
        .catch(() => {})
    },
    parsePoliciesToShow(filter, setVal) {
      let defaultVal = 'never'
      const resetApis = []
      const filterApis = []
      const policies = {}
      if (filter === 'all') {
        this.permissionList.forEach((perm) => {
          resetApis.push(...perm.content)
        })
        defaultVal = setVal
      } else {
        this.permissionList
          .filter((perm) => perm.name === filter)
          .forEach((perm) => {
            filterApis.push(...perm.content)
          })
        this.permissionList
          .filter((perm) => perm.name !== filter)
          .forEach((perm) => {
            resetApis.push(...perm.content)
          })
        filterApis.forEach((api) => {
          policies[api.path] = {}
          api.methods.forEach((method) => {
            policies[api.path][method] = setVal
          })
        })
      }
      resetApis.forEach((api) => {
        policies[api.path] = {}
        api.methods.forEach((method) => {
          policies[api.path][method] = defaultVal
        })
      })
      return policies
    },
    convertPolicies(policies) {
      const result = []
      Object.keys(policies).forEach((key) => {
        this.allMethods.forEach((method) => {
          const pathValue = policies[key][method]
          if (pathValue === 0 || pathValue === 1) {
            result.push({
              path: key,
              method,
              possession: pathValue,
            })
          }
        })
      })
      return result
    },
    findCurrentPermission(name) {
      const _currentPermission = this.permissionList.find((perm) => perm.name === name)
      if (_currentPermission) {
        this.currentPermission = _currentPermission.content
      }
    },
    setPolicies(policies) {
      if (this.record.name === 'root') {
        this.record.policies = this.parsePoliciesToShow('all', 0)
        return
      }
      policies.forEach((policy) => {
        const { path, method, possession } = policy
        // All kuiper
        if (path.indexOf('/kuiper/*') !== -1) {
          this.record.policies = this.parsePoliciesToShow('kuiper', possession)
        } else if (path.indexOf('/neuron/*') !== -1) {
          this.record.policies = this.parsePoliciesToShow('neuron', possession)
        } else if (path.indexOf('/nano/*') !== -1) {
          this.record.policies = this.parsePoliciesToShow('nano', possession)
        } else if (this.record.policies[path]) {
          this.record.policies[path][method] = possession
        }
      })
    },
    renderAuthList({ row, type }) {
      const optionsElement = this.permissionOption.map((item) => <Option value={item.value}>{item.label}</Option>)
      const selectElement = (
        <Select vModel={this.record.policies[row.path][type]} transfer={true} disabled={this.isDetail && !this.isEdit}>
          {optionsElement}
        </Select>
      )
      return row.methods.includes(type) ? selectElement : <div>{this.$t('common.none')}</div>
    },
    save() {
      this.$refs.record.validate((valid) => {
        if (!valid) {
          return
        }
        const { name, policies, description } = this.record
        const data = {
          name,
          description,
          policies: this.convertPolicies(policies),
        }
        if (this.isEdit) {
          updateRole(this.id, data).then(() => {
            this.$Message.success(this.$t('common.updateSuccess'))
          })
        } else {
          createRole(data).then(() => {
            this.$Message.success(this.$t('common.createSuccess'))
          })
        }
        this.$router.push({ path: '/roles' })
      })
    },
  },
}
</script>

<style lang="scss">
.role-details {
  .ivu-card {
    margin-bottom: 20px;
  }
  .permission-title {
    margin-bottom: 16px;
  }
  .permission-card {
    .ivu-table {
      background: transparent;
      border-radius: 0px;
      th {
        background: transparent;
      }
    }
    .ivu-table th,
    .ivu-table td {
      height: 56px;
    }
    .ivu-table-wrapper {
      border-radius: 0px 0px 4px 4px;
    }
    .ivu-card-body {
      padding: 0;
    }
  }
}
</style>
